import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Plus, Search, Clock, CheckCircle, AlertCircle, ChevronRight, ChevronDown,
  User, Users, FileText, Link as LinkIcon, Paperclip, ExternalLink, Eye
} from 'lucide-react';
import { getPlainTextPreview, formatDateSafe, isDeadlineOverdue } from '../utils/textUtils';
import { UserAvatar, ClickableName } from './UserAvatar';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Dimension display names and colors
const DIMENSION_CONFIG = {
  cness_tasks: { name: 'Tasks', color: '#7077FE', icon: '📋' },
  self_care: { name: 'Self Care', color: '#4CAF50', icon: '💚' },
  rnd: { name: 'R&D', color: '#FF9800', icon: '🔬' },
  personal_growth: { name: 'Personal Growth', color: '#9C27B0', icon: '📈' },
  creative: { name: 'Creative', color: '#E91E63', icon: '🎨' }
};

const DimensionTaskView = ({ user, dimension, title, icon: IconComponent, color, hideHeader = false }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [activeSection, setActiveSection] = useState('assigned_to_me');
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedTask, setExpandedTask] = useState(null);
  const [taskData, setTaskData] = useState({
    assigned_to_me: [],
    assigned_by_me: [],
    completed: [],
    counts: { assigned_to_me: 0, assigned_by_me: 0, completed: 0 }
  });

  const fetchTasks = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/tasks/by-dimension/${dimension}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTaskData(response.data);
    } catch (error) {
      console.error('Failed to fetch tasks:', error);
      toast.error('Failed to load tasks');
    } finally {
      setLoading(false);
    }
  }, [dimension]);

  useEffect(() => {
    fetchTasks();
  }, [fetchTasks]);

  const getPriorityColor = (priority) => {
    if (priority >= 8) return '#DC2626';
    if (priority >= 5) return '#D97706';
    return '#059669';
  };

  const getStatusStyle = (status) => {
    switch (status) {
      case 'completed': return { bg: 'rgba(76, 175, 80, 0.1)', color: '#4CAF50' };
      case 'in_progress': return { bg: 'rgba(33, 150, 243, 0.1)', color: '#2196F3' };
      default: return { bg: 'rgba(255, 152, 0, 0.1)', color: '#FF9800' };
    }
  };

  const cleanName = (name) => {
    if (!name) return 'Unknown';
    return name.replace(/\s+\d+$/, '').trim();
  };

  const filterTasks = (tasks) => {
    if (!searchQuery) return tasks;
    const query = searchQuery.toLowerCase();
    return tasks.filter(t =>
      t.title?.toLowerCase().includes(query) ||
      t.description?.toLowerCase().includes(query)
    );
  };

  const sections = [
    { key: 'assigned_to_me', label: 'Assigned to Me', icon: User, color: '#3B82F6' },
    { key: 'assigned_by_me', label: 'Assigned by Me', icon: Users, color: '#8B5CF6' },
    { key: 'completed', label: 'Completed', icon: CheckCircle, color: '#10B981' }
  ];

  const currentTasks = filterTasks(taskData[activeSection] || []);

  const TaskCard = ({ task }) => {
    const isExpanded = expandedTask === task.id;
    const statusStyle = getStatusStyle(task.status);
    const hasResearchNotes = task.research_notes && task.research_notes.trim();
    const hasAttachments = task.attachments && task.attachments.length > 0;
    const hasDocLinks = task.document_links && task.document_links.length > 0;

    return (
      <div
        style={{
          background: 'var(--bg-primary)',
          border: '1px solid var(--border-color)',
          borderRadius: '12px',
          borderLeft: `4px solid ${getPriorityColor(task.priority)}`,
          marginBottom: '12px',
          overflow: 'hidden'
        }}
        data-testid={`task-card-${task.id}`}
      >
        {/* Task Header */}
        <div
          style={{
            padding: '16px',
            cursor: 'pointer',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start'
          }}
          onClick={() => setExpandedTask(isExpanded ? null : task.id)}
        >
          <div style={{ flex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
              <h4 style={{ margin: 0, fontSize: '15px', fontWeight: '600', color: 'var(--text-primary)' }}>
                {task.title}
              </h4>
              <span style={{
                padding: '2px 8px',
                borderRadius: '4px',
                fontSize: '11px',
                fontWeight: '600',
                background: statusStyle.bg,
                color: statusStyle.color,
                textTransform: 'capitalize'
              }}>
                {task.status?.replace('_', ' ')}
              </span>
            </div>
            
            <p style={{ margin: '0 0 8px 0', fontSize: '13px', color: 'var(--text-secondary)', lineHeight: '1.5' }}>
              {getPlainTextPreview(task.description, 120)}
            </p>

            <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
              <span style={{
                padding: '2px 8px',
                borderRadius: '4px',
                fontSize: '11px',
                fontWeight: '600',
                background: `${getPriorityColor(task.priority)}15`,
                color: getPriorityColor(task.priority)
              }}>
                Priority: {task.priority}
              </span>
              
              {task.deadline && (
                <span style={{ fontSize: '12px', color: 'var(--text-tertiary)', display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <Clock size={12} /> {formatDateSafe(task.deadline)}
                </span>
              )}

              {task.assigned_by_user && (
                <span style={{ fontSize: '12px', color: 'var(--text-tertiary)', display: 'flex', alignItems: 'center', gap: '4px' }}>
                  By: <ClickableName 
                    userId={task.assigned_by_user.id || task.assigned_by} 
                    name={task.assigned_by_user.full_name}
                    style={{ fontSize: '12px' }}
                  />
                </span>
              )}

              {(hasResearchNotes || hasAttachments || hasDocLinks) && (
                <div style={{ display: 'flex', gap: '8px' }}>
                  {hasResearchNotes && <FileText size={14} color="#7077FE" title="Has research notes" />}
                  {hasAttachments && <Paperclip size={14} color="#FF9800" title="Has attachments" />}
                  {hasDocLinks && <LinkIcon size={14} color="#4CAF50" title="Has document links" />}
                </div>
              )}
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <button
              onClick={(e) => { e.stopPropagation(); navigate(`/dashboard/tasks/${task.id}`); }}
              style={{
                padding: '8px',
                background: 'rgba(112, 119, 254, 0.1)',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                color: '#7077FE'
              }}
              title="View details"
            >
              <Eye size={16} />
            </button>
            {isExpanded ? <ChevronDown size={20} color="var(--text-tertiary)" /> : <ChevronRight size={20} color="var(--text-tertiary)" />}
          </div>
        </div>

        {/* Expanded Content */}
        {isExpanded && (
          <div style={{ padding: '0 16px 16px 16px', borderTop: '1px solid var(--border-color)' }}>
            {/* Assigned Users */}
            {task.assigned_to_users && task.assigned_to_users.length > 0 && (
              <div style={{ marginTop: '12px' }}>
                <div style={{ fontSize: '12px', color: 'var(--text-tertiary)', marginBottom: '6px', fontWeight: '600' }}>Assigned To:</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                  {task.assigned_to_users.map((u, idx) => (
                    <span key={idx} style={{
                      padding: '4px 10px',
                      background: 'var(--bg-secondary)',
                      borderRadius: '16px',
                      fontSize: '12px',
                      color: 'var(--text-primary)'
                    }}>
                      {cleanName(u.full_name)}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Research Notes */}
            {hasResearchNotes && (
              <div style={{ marginTop: '16px' }}>
                <div style={{ fontSize: '12px', color: 'var(--text-tertiary)', marginBottom: '8px', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <FileText size={14} /> Research Notes:
                </div>
                <div
                  style={{
                    padding: '12px',
                    background: 'var(--bg-secondary)',
                    borderRadius: '8px',
                    fontSize: '13px',
                    lineHeight: '1.6',
                    color: 'var(--text-primary)'
                  }}
                  dangerouslySetInnerHTML={{ __html: task.research_notes }}
                />
              </div>
            )}

            {/* Attachments */}
            {hasAttachments && (
              <div style={{ marginTop: '16px' }}>
                <div style={{ fontSize: '12px', color: 'var(--text-tertiary)', marginBottom: '8px', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <Paperclip size={14} /> Attachments ({task.attachments.length}):
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                  {task.attachments.map((att, idx) => (
                    <div key={idx} style={{
                      padding: '8px 12px',
                      background: 'var(--bg-secondary)',
                      borderRadius: '8px',
                      fontSize: '12px',
                      color: 'var(--text-primary)',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px'
                    }}>
                      <Paperclip size={12} />
                      {att.filename || `Attachment ${idx + 1}`}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Document Links */}
            {hasDocLinks && (
              <div style={{ marginTop: '16px' }}>
                <div style={{ fontSize: '12px', color: 'var(--text-tertiary)', marginBottom: '8px', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <LinkIcon size={14} /> Document Links:
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  {task.document_links.map((doc, idx) => (
                    <a
                      key={idx}
                      href={doc.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        padding: '8px 12px',
                        background: 'var(--bg-secondary)',
                        borderRadius: '8px',
                        textDecoration: 'none',
                        color: '#7077FE',
                        fontSize: '13px'
                      }}
                    >
                      <ExternalLink size={14} />
                      {doc.title || doc.url}
                    </a>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div style={{ padding: '40px', textAlign: 'center' }}>
        <div className="loading-spinner"></div>
        <p style={{ marginTop: '16px', color: 'var(--text-secondary)' }}>Loading tasks...</p>
      </div>
    );
  }

  return (
    <div className={hideHeader ? '' : 'content-area'} data-testid={`dimension-view-${dimension}`}>
      {/* Header */}
      {!hideHeader && (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            {IconComponent && <IconComponent size={28} color={color} />}
            <div>
              <h1 style={{ margin: 0, fontSize: '24px', fontWeight: '700', color: 'var(--text-primary)' }}>{title}</h1>
              <p style={{ margin: '4px 0 0 0', fontSize: '14px', color: 'var(--text-secondary)' }}>
                {taskData.counts.assigned_to_me + taskData.counts.assigned_by_me} active · {taskData.counts.completed} completed
              </p>
            </div>
          </div>
          <button
            onClick={() => navigate(`/dashboard/tasks/new?dimension=${dimension}`)}
            className="gradient-btn"
            style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '12px 20px' }}
            data-testid="create-task-btn"
          >
            <Plus size={18} /> Create Task
          </button>
        </div>
      )}

      {/* Stats Bar (for when header is hidden) */}
      {hideHeader && (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <div style={{ display: 'flex', gap: '16px', fontSize: '14px', color: 'var(--text-secondary)' }}>
            <span>{taskData.counts.assigned_to_me + taskData.counts.assigned_by_me} active tasks</span>
            <span>·</span>
            <span>{taskData.counts.completed} completed</span>
          </div>
          <button
            onClick={() => navigate(`/dashboard/tasks/new?dimension=${dimension}`)}
            className="gradient-btn"
            style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 18px' }}
            data-testid="create-task-btn"
          >
            <Plus size={16} /> Create Task
          </button>
        </div>
      )}

      {/* Search */}
      <div style={{ marginBottom: '20px', position: 'relative' }}>
        <Search size={18} style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-tertiary)' }} />
        <input
          type="text"
          placeholder="Search tasks..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          style={{
            width: '100%',
            padding: '12px 12px 12px 44px',
            border: '1px solid var(--border-color)',
            borderRadius: '10px',
            fontSize: '14px',
            background: 'var(--bg-primary)',
            color: 'var(--text-primary)'
          }}
          data-testid="search-input"
        />
      </div>

      {/* Section Tabs */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '24px', flexWrap: 'wrap' }}>
        {sections.map(section => (
          <button
            key={section.key}
            onClick={() => setActiveSection(section.key)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '10px 18px',
              border: activeSection === section.key ? 'none' : '1px solid var(--border-color)',
              borderRadius: '10px',
              background: activeSection === section.key ? section.color : 'var(--bg-primary)',
              color: activeSection === section.key ? 'white' : 'var(--text-primary)',
              cursor: 'pointer',
              fontWeight: '500',
              fontSize: '14px',
              transition: 'all 0.2s'
            }}
            data-testid={`section-tab-${section.key}`}
          >
            <section.icon size={16} />
            {section.label}
            <span style={{
              padding: '2px 8px',
              borderRadius: '12px',
              fontSize: '12px',
              fontWeight: '600',
              background: activeSection === section.key ? 'rgba(255,255,255,0.2)' : 'var(--bg-secondary)'
            }}>
              {taskData.counts[section.key]}
            </span>
          </button>
        ))}
      </div>

      {/* Task List */}
      {currentTasks.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '60px 20px', color: 'var(--text-tertiary)' }}>
          <AlertCircle size={48} style={{ marginBottom: '16px', opacity: 0.5 }} />
          <p style={{ margin: 0, fontSize: '16px' }}>
            {searchQuery ? 'No tasks match your search' : `No tasks in "${sections.find(s => s.key === activeSection)?.label}"`}
          </p>
        </div>
      ) : (
        <div>
          {currentTasks.map(task => (
            <TaskCard key={task.id} task={task} />
          ))}
        </div>
      )}
    </div>
  );
};

export default DimensionTaskView;
